/**
 * Russian translations for the Tourist Tools application.
 * Organized by feature namespace.
 */
export const RU = {
  // ============================================
  // EXISTING NAMESPACES (preserved)
  // ============================================

  // Scraped content list
  scraped: {
    // Page header
    title: 'Собранный контент',
    description: 'Просмотр и управление собранным контентом из источников данных',

    // Column headers
    columns: {
      name: 'Название',
      type: 'Тип',
      quality: 'Качество',
      status: 'Статус',
      sourceUrl: 'URL источника',
      dataSource: 'Источник данных',
      scraped: 'Дата',
      actions: 'Действия',
      description: 'Описание',
    },

    // Status badges
    status: {
      pending: 'ОЖИДАЕТ',
      converted: 'КОНВЕРТИРОВАНО',
      failed: 'ОШИБКА',
      skipped: 'ПРОПУЩЕНО',
    },

    // Filter controls
    filters: {
      qualityScore: 'Оценка качества',
      min: 'Мин',
      max: 'Макс',
      contentType: 'Тип контента',
      allTypes: 'Все типы',
      status: 'Статус',
      allStatuses: 'Все статусы',
      pending: 'Ожидает',
      clearFilter: 'Сбросить фильтр',
      clearDataSourceFilter: 'Сбросить фильтр источника',
    },

    // Search controls
    search: {
      placeholder: 'Поиск по названию или описанию...',
      clear: 'Очистить поиск',
    },

    // Pagination
    pagination: {
      showing: 'Показано {count} из {total}',
      previous: 'Назад',
      next: 'Далее',
      page: 'Страница {current} из {total}',
    },

    // Expanded row
    expanded: {
      extractedData: 'Извлечённые данные',
      conversionError: 'Ошибка конвертации',
      convertedToPoi: 'Конвертировано в POI #',
    },

    // Empty states
    empty: {
      noContent: 'Нет собранного контента',
      noContentForSource: 'Нет собранного контента для этого источника',
      sourceDeleted: 'Источник удалён',
    },

    // Actions
    actions: {
      view: 'Просмотр',
      convert: 'Конвертировать',
      converting: 'Конвертация...',
      expand: 'Развернуть',
      collapse: 'Свернуть',
    },

    // Quality metrics dashboard
    metrics: {
      loadError: 'Ошибка загрузки метрик качества',
      loadFailed: 'Не удалось загрузить метрики',
      totalScraped: 'Всего собрано',
      avgQualityScore: 'Средняя оценка качества',
      conversionRate: 'Коэффициент конвертации',
      byStatus: 'По статусу',
      byContentType: 'По типу контента',
      byDataSource: 'По источнику данных',
      sourceNumber: 'Источник #{id}',
      noDataSources: 'Источники данных не найдены',
    },

    // Detail modal
    detail: {
      title: 'Детали собранного контента',
      loadError: 'Ошибка загрузки деталей контента',
      loadFailed: 'Не удалось загрузить детали контента',
      quality: 'Качество: {score}%',
      sourceInfo: 'Информация об источнике',
      sourceUrl: 'URL источника',
      dataSource: 'Источник данных',
      sourceNumber: 'Источник #{id}',
      scrapedAt: 'Дата сбора',
      convertedAt: 'Дата конвертации',
      poiId: 'ID POI',
      conversionError: 'Ошибка конвертации',
      extractedData: 'Извлечённые данные (JSON)',
    },
  },

  // Conversion messages
  conversion: {
    // Single conversion
    success: 'Успешно конвертировано',
    failed: 'Ошибка конвертации: {message}',
    alreadyConverted: 'Уже конвертировано',
    skipped: 'Пропущено: {reason}',
    recommendation: 'Рекомендация:',
    technicalDetails: 'Технические детали',

    // Quality warning modal
    qualityWarning: {
      title: 'Низкое качество контента',
      message: 'Качество контента ({score}%) ниже порога 70%. Продолжить конвертацию?',
      confirm: 'Продолжить',
      cancel: 'Отмена',
    },

    // Batch conversion
    batch: {
      button: 'Конвертировать выбранное ({count})',
      converting: 'Конвертация...',
      result: 'Результат конвертации: Успешно: {succeeded}, Ошибок: {failed}, Пропущено: {skipped}',
      selectAll: 'Выбрать все',
      deselectAll: 'Снять выбор',
      errorDetailsTitle: 'Ошибки конвертации',
      errorItem: 'ID {id}: {message}',
      batchId: 'ID группы: {batchId}',
      rollback: 'Отменить группу',
      rollbackConfirm: 'Отменить конвертацию группы {batchId}? Будут удалены {count} созданных POI.',
      rollbackSuccess: 'Группа отменена: удалено {poisDeleted} POI, сброшено {contentsReset} записей',
      rollbackFailed: 'Ошибка отмены группы: {message}',
      lowQualityWarning: '{count} из {total} выбранных имеют качество ниже 70%. Принудительная конвертация может создать ненадёжные POI.',
      forceConvertBatch: 'Конвертировать всё ({count})',
    },
  },

  // Common messages
  common: {
    loading: 'Загрузка...',
    error: 'Произошла ошибка',
    loadingFailed: 'Не удалось загрузить контент',
    retry: 'Повторить',
    close: 'Закрыть',
    yes: 'Да',
    no: 'Нет',
    save: 'Сохранить',
    saving: 'Сохранение...',
    cancel: 'Отменить',
    select: 'Выбрать',
    selected: 'Выбран',
    edit: 'Редактировать',
    delete: 'Удалить',
    create: 'Создать',
    update: 'Обновить',
    add: 'Добавить',
    actions: 'Действия',
  },

  // Content types
  contentTypes: {
    HOTEL: 'Отель',
    ATTRACTION: 'Достопримечательность',
    RESTAURANT: 'Ресторан',
    TRANSPORT: 'Транспорт',
    DESTINATION: 'Направление',
    CITY: 'Город',
    TRANSPORT_HUB: 'Транспортный узел',
  },

  // Map popup labels
  map: {
    address: 'Адрес:',
    coordinates: 'Координаты:',
    loadingRoute: 'Загрузка маршрута...',
    waypointsPlaceholder: 'Точки маршрута появятся здесь по мере общения',
  },

  // Chat messages
  chat: {
    source: 'Источник:',
    title: 'Помощник путешественника',
    connected: 'Подключено',
    reconnecting: 'Переподключение...',
    connecting: 'Подключение...',
    aiResponding: 'ИИ отвечает...',
    defaultPlaceholder: 'Спросите о направлениях, отелях, активностях...',
    emptyState: 'Начните беседу, чтобы спланировать поездку!',
    emptyHint: 'Попробуйте: «Хочу посетить пляжи Италии на 5 дней»',
    send: 'Отправить',
    typeMessage: 'Введите сообщение...',
  },

  // Waypoints list
  waypoints: {
    header: 'Точки маршрута',
    count: '{count} точек',
    emptyState: 'Точек пока нет. Пообщайтесь с ИИ, чтобы добавить места в маршрут.',
    singleWaypointHint: 'Добавьте ещё точки, чтобы создать маршрут.',
    removeConfirm: 'Убрать «{name}» из маршрута?',
    moveUp: 'Вверх',
    moveDown: 'Вниз',
    remove: 'Убрать',
  },

  // ============================================
  // NEW NAMESPACES
  // ============================================

  // Authentication
  auth: {
    login: {
      title: 'Вход в систему',
      username: 'Имя пользователя',
      password: 'Пароль',
      submit: 'Войти',
      submitting: 'Вход...',
      error: 'Неверное имя пользователя или пароль',
      email: 'Email',
      emailPlaceholder: 'Введите email',
      passwordPlaceholder: 'Введите пароль',
      noAccount: 'Нет аккаунта?',
      registerLink: 'Зарегистрироваться',
    },
    social: {
      divider: 'Войти через соцсети',
      vk: 'Войти через VK',
      yandex: 'Войти через Яндекс',
      mailru: 'Войти через Mail.ru',
    },
    oauth: {
      complete: {
        title: 'Ещё один шаг',
        description: 'Сервис не передал ваш email. Укажите его, чтобы завершить вход.',
        email: 'Email',
        emailPlaceholder: 'Введите email',
        submit: 'Продолжить',
        submitting: 'Сохранение...',
        error: 'Не удалось завершить вход. Попробуйте снова.',
        noPending: 'Сессия входа истекла. Начните вход через соцсеть заново.',
      },
      errors: {
        not_configured: 'Вход через этот сервис пока недоступен.',
        access_denied: 'Вход через соцсеть был отменён.',
        missing_code: 'Сервис авторизации не вернул код. Попробуйте снова.',
        token_exchange_failed: 'Не удалось связаться с сервисом авторизации.',
        userinfo_failed: 'Не удалось получить данные профиля из соцсети.',
        invalid_state: 'Сессия входа устарела. Попробуйте снова.',
        unknown_provider: 'Неизвестный сервис авторизации.',
        internal_error: 'Внутренняя ошибка при входе через соцсеть.',
        default: 'Не удалось войти через соцсеть. Попробуйте снова.',
      },
    },
    showPassword: 'Показать пароль',
    hidePassword: 'Скрыть пароль',
    validation: {
      emailValid: 'Email корректен',
      emailInvalid: 'Некорректный формат email',
      passwordValid: 'Пароль корректен',
      passwordTooShort: 'Минимум 6 символов',
      passwordsMatch: 'Пароли совпадают',
      passwordsMismatch: 'Пароли не совпадают',
    },
    register: {
      title: 'Регистрация',
      email: 'Email',
      emailPlaceholder: 'Введите email',
      password: 'Пароль',
      passwordPlaceholder: 'Введите пароль',
      confirmPassword: 'Подтвердите пароль',
      confirmPasswordPlaceholder: 'Повторите пароль',
      submit: 'Зарегистрироваться',
      submitting: 'Регистрация...',
      hasAccount: 'Уже есть аккаунт?',
      signIn: 'Войти',
      passwordHint: 'Минимум 8 символов, заглавная и строчная буквы, цифра и спецсимвол',
      success: 'Регистрация успешна! Пожалуйста, войдите.',
      error: 'Ошибка регистрации',
      genericError: 'Произошла ошибка при регистрации',
    },
  },

  passwordReset: {
    title: 'Сброс пароля',
    newPassword: 'Новый пароль',
    confirmPassword: 'Подтвердите пароль',
    submit: 'Сохранить новый пароль',
    success: 'Пароль успешно изменён! Теперь вы можете войти.',
    error: {
      invalidToken: 'Недействительная или использованная ссылка для сброса пароля',
      expired: 'Срок действия ссылки истёк. Запросите сброс пароля повторно.',
      mismatch: 'Пароли не совпадают',
      tooShort: 'Пароль: минимум 8 символов, заглавная и строчная буквы, цифра и спецсимвол',
      generic: 'Произошла ошибка. Попробуйте позже.',
    },
    requestTitle: 'Восстановление пароля',
    requestDescription: 'Введите ваш email. Мы отправим ссылку для сброса пароля.',
    requestEmail: 'Email',
    requestSubmit: 'Отправить ссылку',
    requestSuccess: 'Если аккаунт с таким email существует, мы отправили ссылку для сброса пароля.',
    backToLogin: 'Вернуться к входу',
  },

  // Shared transport types (used in multiple components) + Phase 61 suggestion panel keys
  transport: {
    CAR: 'Автомобиль',
    TRAIN: 'Поезд',
    PLANE: 'Самолет',
    WALKING: 'Пеший',
    MIXED: 'Смешанный',
    // Phase 61 — Solo "Поехать" mock transport suggestion panel
    panelHeading: 'Варианты транспорта',
    demoBadge: 'Демо-режим',
    typeLabel: { train: 'Поезд', plane: 'Самолёт', bus: 'Автобус' },
    bookButton: 'Забронировать',
    bookedButton: 'Забронировано',
    bookSuccessToast: 'Заявка отправлена (демо)',
    bookError: 'Не удалось отправить заявку. Попробуйте ещё раз.',
    from: 'Откуда:',
    to: 'Куда:',
    loadError: 'Не удалось загрузить варианты',
    retry: 'Попробовать снова',
    empty: 'Варианты недоступны для этого направления.',
  },

  // Shared travel formats (used in multiple components)
  travelFormat: {
    INDIVIDUAL: 'Индивидуальный',
    GROUP: 'Групповой',
    MINI_GROUP: 'Минигруппа',
  },

  // Route source labels
  routeSource: {
    PARTNER: 'От организатора',
    AI_COMPOSED: 'Составлен ИИ',
    GUIDE_AUTHORED: 'с гидом',
  },

  guideRoute: {
    badge: 'с гидом',
    pricePerPerson: 'от {price}₽/чел',
    donation: 'Донат',
    guideProfileHeading: 'Ваш гид',
    pricingHeading: 'Стоимость участия',
    pricePerPersonLabel: 'Цена за человека',
    pricePerPersonValue: '{price}₽',
    groupSizeLabel: 'Размер группы',
    groupSizeValue: '{min}–{max} чел.',
    soloFallbackLabel: 'Индивидуально (соло)',
    soloFallbackValue: '{price}₽',
    totalLabel: 'Итого для {count} чел.',
    totalValue: '{price}₽',
    guidePageLink: 'Страница гида →',
    ratingAriaLabel: 'Рейтинг {value} из 5',
    badgeAriaLabel: 'Маршрут с гидом',
  },

  // Route editing
  routeEdit: {
    editMode: 'Редактирование маршрута',
    editRoute: 'Редактировать',
    addPoi: 'Добавить точку',
    removePoi: 'Удалить',
    searchPlaceholder: 'Поиск достопримечательностей...',
    noResults: 'Ничего не найдено',
    tryDifferentSearch: 'Попробуйте другой запрос',
    undoRemove: 'Отменить',
    waypointRemoved: 'Точка удалена',
    minWaypointsWarning: 'Маршрут должен содержать минимум 2 точки',
    duplicateWarning: 'Эта точка уже добавлена в маршрут',
    saveChanges: 'Сохранить изменения',
    cancelEdit: 'Отменить',
    doneEditing: 'Готово',
    dragToReorder: 'Перетащите для изменения порядка',
    poiAdded: 'Точка добавлена',
    errorSaving: 'Ошибка сохранения изменений',
    searchPois: 'Поиск точек интереса',
    selectToAdd: 'Выберите точку для добавления',
    noRegionPois: 'В этом регионе нет точек интереса',
    typeToFilter: 'Начните вводить для фильтрации',
    publishValidation: {
      bannerTitle: 'Не удалось опубликовать — исправьте:',
      saveFailed: 'Не удалось сохранить черновик',
      publishFailed: 'Не удалось опубликовать маршрут',
      loadFailed: 'Не удалось загрузить маршрут',
      archiveNotSaved: 'Нечего архивировать — маршрут ещё не сохранён',
      archiveFailed: 'Не удалось архивировать маршрут',
      deleteFailed: 'Не удалось удалить маршрут',
      waypointsMinPois: 'Маршрут должен содержать минимум 2 точки',
      regionRequired: 'Укажите регион в разделе «Детали»',
      monetizationRequired: 'Выберите тип монетизации в разделе «Цена и группа»',
      priceRequired: 'Укажите цену за человека',
      soloPriceRequired: 'Укажите цену для индивидуального прохождения',
      titleInvalid: 'Название маршрута должно содержать от 3 до 120 символов',
      descriptionInvalid: 'Описание не должно превышать 4000 символов',
      difficultyInvalid: 'Сложность должна быть: лёгкая, средняя или сложная',
      estimatedDurationMinutesInvalid: 'Продолжительность должна быть от 1 до 10080 минут',
      waypointsInvalid: 'Превышено максимальное количество точек (100)',
    },
  },

  // Route planning form
  routes: {
    planning: {
      title: 'Планирование маршрута',
      destination: 'Пункт назначения',
      destinationPlaceholder: 'Введите город или страну',
      startDate: 'Дата начала',
      endDate: 'Дата окончания',
      format: 'Формат путешествия',
      transport: 'Транспорт',
      numberOfPeople: 'Количество человек',
      submit: 'Создать маршруты',
      submitting: 'Создание маршрутов...',
    },
    input: {
      title: 'Куда отправимся?',
      subtitle: 'Расскажите о своих планах, и мы подберем идеальный маршрут',
      whereLabel: 'Куда?',
      whenLabel: 'Когда?',
      formatLabel: 'Формат?',
      transportLabel: 'На чем?',
      destinationPlaceholder: 'Город или страна',
      submit: 'Найти маршруты',
      submitting: 'Ищем маршруты...',
    },
    card: {
      transport: 'Транспорт:',
      format: 'Формат:',
      days: 'Дней:',
      people: 'Человек:',
      priceOnRequest: 'Цена по запросу',
      routeHeading: 'Маршрут',
      moreDetails: 'Подробнее на сайте',
    },
    list: {
      notFound: 'Маршруты не найдены. Попробуйте изменить параметры поиска.',
      foundCount: 'Найдено {count} {variants} маршрута',
    },
    variants: {
      title: 'Варианты маршрутов',
      description: 'Мы подобрали для вас {count} варианта. Выберите подходящий.',
      variantLabel: 'Вариант {number}',
      daysCount: '{count} дней',
      routeHeading: 'Маршрут:',
    },
    detail: {
      title: 'Детали маршрута',
      subtitle: 'Ваш выбранный маршрут',
      partnerRoute: 'Партнерский маршрут',
      priceOnRequest: 'Цена по запросу',
      datesOnRequest: 'Даты по запросу',
      routeType: 'Тип маршрута',
      format: 'Формат',
      duration: 'Продолжительность',
      peopleCount: 'Количество человек',
      dates: 'Даты проведения',
      cost: 'Стоимость',
      keyWaypoints: 'Ключевые точки маршрута',
      activities: 'Возможные активности',
      organizer: 'Организатор',
      organizerWebsite: 'Сайт организатора',
      additionalDetails: 'Дополнительные сведения',
      goToWebsite: 'Перейти на сайт маршрута',
      customize: 'Добавить условия',
      mapPlaceholder: 'Карта маршрута будет доступна после уточнения координат',
      loadingRoute: 'Загрузка маршрута...',
    },
  },

  // Navigation
  navigation: {
    planning: 'Планирование',
    chatAssistant: 'Чат-ассистент',
    admin: 'Админ',
    profile: 'Профиль',
    savedRoutes: 'Сохранённые маршруты',
    favorites: 'Избранное',
    signOut: 'Выйти',
    signIn: 'Войти',
    userMenu: 'Меню пользователя',
    walkRoutes: 'Прогулочные маршруты',
    gpxImport: 'Импорт GPX',
    poiManagement: 'Управление POI',
    guideCabinet: 'Кабинет гида',
    aiAssistant: 'AI Ассистент',
    myTrips: 'Мои поездки',
    darkTheme: 'Тёмная тема',
    lightTheme: 'Светлая тема',
    notifications: 'Уведомления',
    mainNav: 'Основная навигация',
    language: 'Язык',
  },

  // My Trips stub page
  myTrips: {
    comingSoon: 'Скоро',
    comingSoonBody: 'Раздел «Мои поездки» появится в ближайшем обновлении.',
  },

  // Walk Routes user-facing page (v1.3)
  walkRoutes: {
    title: 'Прогулочные маршруты',
    selectTerritory: 'Выберите территорию',
    moreComingSoon: 'Скоро появятся ещё маршруты',
    noRoutes: 'Для этой территории пока нет маршрутов',
    backToSuggestions: 'Назад к предложениям',
    distance: 'Расстояние',
    duration: 'Время прогулки',
    loading: 'Загрузка...',
    canMergeWith: 'Похожие маршруты',
    crossingPoint: 'Точка пересечения',
    mergeButton: 'Объединить',
    mergePreviewTitle: 'Предпросмотр объединённого маршрута',
    mergeConfirm: 'Подтвердить объединение',
    mergeCancel: 'Отмена',
    mergeInProgress: 'Объединение...',
    mergeFailed: 'Объединение не удалось',
    mergeInfeasible: 'Объединение невозможно',
    mergeDistance: 'Расстояние',
    mergePoints: 'Точек',
    generatingDescription: 'Генерация описания...',
    mergeSuccess: 'Маршруты успешно объединены',
    poiBadge: 'POI',
    nearbyPois: 'Достопримечательности рядом',
    // Walk route comparison (v1.4 Phase 25)
    compareButton: 'Сравнить',
    compareBackToSuggestions: 'Назад к предложениям',
    comparisonAnalysisLoading: 'Анализируем маршруты с помощью ИИ...',
    comparisonAnalysisError: 'Не удалось получить сравнительный анализ',
    comparisonAnalysisTitle: 'Сравнительный анализ',
    comparisonSelectTwo: 'Выберите 2 маршрута для сравнения',
    comparisonPoiCount: 'POI рядом',
    // Phase 26: merge recommendation badge labels
    mergeRecommendLabel: 'Рекомендация по объединению',
    mergeVerdictRecommend: 'Рекомендуем объединить',
    mergeVerdictMaybe: 'Возможно, но есть нюансы',
    mergeVerdictNotRecommend: 'Не рекомендуем',
    estimatedCombinedDuration: 'Общее время:',
    // Phase 27: merge preview and save strings
    mergePreviewButton: 'Предпросмотр',
    mergeSourceComparison: 'Итого',
    mergeSaveButton: 'Сохранить в коллекцию',
    mergeSaving: 'Сохранение...',
    mergeNameLabel: 'Название маршрута',
    mergeNamePlaceholder: 'Введите название маршрута',
    mergeSaveSuccess: 'Маршрут сохранён в коллекцию',
    mergeSavedRoutesLink: 'Перейти к сохранённым маршрутам',
    mergeLoginRequired: 'Войдите, чтобы сохранить маршрут в коллекцию',
    mergePreviewMergeButton: 'Предпросмотр объединения',
    mergeDuplicateName: 'Маршрут с таким названием уже существует',
    // Phase 30: i18n gap closure — error messages, units, detail labels
    errors: {
      loadSuggestions: 'Не удалось загрузить маршруты. Попробуйте снова.',
      loadDetail: 'Не удалось загрузить детали маршрута.',
      loadComparison: 'Не удалось загрузить маршруты',
      mergePreviewLoad: 'Ошибка загрузки предпросмотра',
    },
    selectPlaceholder: '— выберите —',
    territory: 'Территория',
    addedDate: 'Добавлен',
    noDescription: 'Описание отсутствует.',
    units: {
      km: 'км',
      min: 'мин',
      hours: 'ч',
    },
  },

  // Footer
  footer: {
    tagline: 'Откройте идеальное путешествие с помощью ИИ-планирования.',
    product: 'Продукт',
    planning: 'Планирование',
    chatAssistant: 'Чат-ассистент',
    company: 'Компания',
    aboutUs: 'О нас',
    contacts: 'Контакты',
    copyright: 'Все права защищены.',
  },

  // Validation messages
  validation: {
    required: '{field} обязателен',
    emailRequired: 'Email обязателен',
    emailInvalid: 'Некорректный формат email',
    passwordRequired: 'Пароль обязателен',
    passwordMinLength: 'Пароль должен содержать минимум 8 символов',
    passwordComplexity: 'Пароль должен содержать заглавную и строчную буквы, цифру и специальный символ',
    passwordMismatch: 'Пароли не совпадают',
    destinationMinLength: 'Укажите пункт назначения (минимум 2 символа)',
    startDateRequired: 'Укажите дату начала',
    startDatePast: 'Дата начала не может быть в прошлом',
    endDateRequired: 'Укажите дату окончания',
    endDateAfterStart: 'Дата окончания должна быть после даты начала',
    selectTransport: 'Выберите хотя бы один вид транспорта',
    minPeople: 'Количество человек должно быть не менее 1',
    avgVisitTimeRange: 'Значение должно быть от 1 до 480 минут',
  },

  // Error pages and messages
  errors: {
    notFound: {
      title: 'Страница не найдена',
      message: 'Страница, которую вы ищете, не существует или была перемещена.',
      goHome: 'На главную',
    },
    generic: 'Произошла ошибка',
    loadingFailed: 'Не удалось загрузить данные',
    routeCreation: 'Ошибка при создании маршрутов',
    routeSelection: 'Ошибка при выборе маршрута',
  },

  // Data source detail page
  dataSourceDetail: {
    breadcrumb: {
      scrapedContent: 'Собранный контент',
      source: 'Источник: {name}',
    },
    config: {
      title: 'Конфигурация источника',
      url: 'URL',
      status: 'Статус',
      parserType: 'Тип парсера',
      selectors: 'Селекторы',
      cacheTtl: 'Кэш (часы)',
      cronSchedule: 'Расписание',
      lastScrapedAt: 'Последний сбор',
      lastSuccessAt: 'Последний успешный',
      createdAt: 'Создан',
      createdBy: 'Создал',
      noSelectors: 'Нет селекторов',
    },
    loading: 'Загрузка источника данных...',
    loadError: 'Не удалось загрузить источник данных',
    notFound: 'Источник данных не найден',
    contentTitle: 'Контент из этого источника',
  },

  // Admin section
  admin: {
    // Page header
    title: 'Панель администратора',

    // Tab labels
    tabs: {
      users: 'Управление пользователями',
      dataSources: 'Источники данных',
      scrapedContent: 'Собранный контент',
      duplicates: 'Дубликаты',
      conversionJobs: 'Авто-конвертация',
      transportSettings: 'Настройки транспорта',
    },

    // Sidebar labels
    sidebar: {
      pois: 'Точки интереса',
      harvest: 'Сбор данных',
      duplicates: 'Дубликаты',
      gpxRoutes: 'GPX маршруты',
      transport: 'Настройки транспорта',
      users: 'Пользователи',
      guides: 'Гиды',
      conversion: 'Авто-конвертация',
      poiDiscovery: 'Поиск POI по области',
      groupContent: 'Контент',
      groupRoutes: 'Маршруты',
      groupSystem: 'Система',
      collapse: 'Свернуть боковую панель',
      expand: 'Развернуть боковую панель',
      duplicateBadge: '{N} дубликатов ожидают проверки',
    },

    // Area-based POI discovery
    poiDiscovery: {
      pageTitle: 'Поиск POI по области',
      pageDescription: 'Нарисуйте квадратную область на карте и запустите автоматический поиск точек интереса.',
      sectionMap: 'Область поиска',
      sectionProgress: 'Прогресс задания',
      drawHint: 'Кликните и перетащите на карте, чтобы нарисовать область поиска.',
      sizeLabel: '{size} × {size} км',
      sizeExceeded: 'Размер области превышает максимальный ({max} км). Область уменьшена.',
      discoverButton: 'Запустить поиск',
      discoverButtonLoading: 'Запуск...',
      configError: 'Не удалось загрузить конфигурацию поиска',
      startError: 'Не удалось запустить задание поиска',
      jobError: 'Ошибка задания поиска',
      statusPending: 'Ожидание',
      statusScraping: 'Запрос провайдеров',
      statusProcessing: 'Обработка результатов',
      statusCompleted: 'Завершено',
      statusFailed: 'Ошибка',
      progressCreated: 'Создано POI: {count}',
      progressSkipped: 'Пропущено: {count}',
      progressFailed: 'Ошибок: {count}',
      summaryToast: 'Поиск завершён: создано {created}, пропущено {skipped}',
      summaryProvider: 'Провайдеры: {summary}',
      viewPoisLink: 'Просмотреть созданные POI',
      accessDenied: 'Доступ запрещён. Функция недоступна для вашего аккаунта.',
      loadingConfig: 'Загрузка конфигурации...',
    },

    // Transport config
    transportConfig: {
      title: 'Настройки радиуса транспорта',
      description: 'Настройте радиус покрытия для каждого вида транспорта. Эти значения используются при фильтрации точек интереса для построения маршрутов.',
      loading: 'Загрузка конфигураций...',
      loadError: 'Ошибка загрузки конфигураций транспорта',
      notFound: 'Конфигурации транспорта не найдены.',
      radiusLabel: 'Радиус покрытия (км)',
      active: 'Активен',
      inactive: 'Неактивен',
      saveError: 'Ошибка сохранения',
      radiusUpdated: 'Радиус для "{type}" обновлён',
      radiusMin: 'Радиус должен быть не менее {min} км',
      radiusMax: 'Радиус должен быть не более {max} км',
      showScrapedContent: 'Показать собранный контент',
      min: 'Мин: {value} км',
      max: 'Макс: {value} км',
      updated: 'Обновлено: {date}',
      unit: 'км',
    },

    // User management
    users: {
      title: 'Пользователи',
      createUser: 'Создать пользователя',
      editUser: 'Редактировать пользователя: {username}',
      createNewUser: 'Создать нового пользователя',
      noUsers: 'Пользователи не найдены',
      columns: {
        username: 'Имя пользователя',
        displayName: 'Отображаемое имя',
        email: 'Email',
        role: 'Роль',
        status: 'Статус',
        created: 'Создан',
        actions: 'Действия',
      },
      status: {
        enabled: 'Активен',
        disabled: 'Отключён',
      },
      roles: {
        user: 'Пользователь',
        admin: 'Администратор',
        guide: 'Гид',
      },
      form: {
        usernameLabel: 'Имя пользователя *',
        usernamePlaceholder: 'Введите имя пользователя (3-50 символов)',
        passwordLabel: 'Пароль *',
        passwordPlaceholder: 'Введите пароль',
        roleLabel: 'Роль',
        displayNameLabel: 'Отображаемое имя',
        displayNamePlaceholder: 'Введите отображаемое имя (необязательно)',
        emailLabel: 'Email',
        emailPlaceholder: 'Введите email (необязательно)',
        accountEnabled: 'Аккаунт активен',
        passwordNote: 'Примечание: Смена пароля осуществляется через отдельную процедуру сброса пароля.',
      },
      validation: {
        usernameLength: 'Имя пользователя должно быть от 3 до 50 символов',
        passwordRequired: 'Пароль обязателен',
        passwordComplexity: 'Пароль должен содержать минимум 8 символов, включая заглавные и строчные буквы, цифру и специальный символ',
        emailInvalid: 'Некорректный формат email',
      },
      passwordStrength: {
        weak: 'Слабый',
        medium: 'Средний',
        strong: 'Надёжный',
      },
      messages: {
        loadError: 'Ошибка загрузки пользователей',
        createSuccess: 'Пользователь успешно создан',
        createError: 'Ошибка создания пользователя',
        updateSuccess: 'Пользователь успешно обновлён',
        updateError: 'Ошибка обновления пользователя',
        deleteConfirm: 'Вы уверены, что хотите удалить пользователя "{username}"? Это удалит пользователя и все его данные.',
        deleteSuccess: 'Пользователь "{username}" успешно удалён',
        deleteError: 'Ошибка удаления пользователя',
        cannotDeleteLastAdmin: 'Невозможно удалить последнего администратора',
      },
    },

    // Data sources
    dataSources: {
      title: 'Источники данных',
      addDataSource: 'Добавить источник',
      addNewDataSource: 'Добавить новый источник данных',
      editDataSource: 'Редактировать источник данных',
      noDataSources: 'Источники данных не настроены.',
      addFirstDataSource: 'Добавить первый источник данных',
      loading: 'Загрузка источников данных...',
      columns: {
        url: 'URL',
        description: 'Описание',
        status: 'Статус',
        lastScraped: 'Последний сбор',
        lastSuccess: 'Последний успех',
        createdBy: 'Создал',
        actions: 'Действия',
      },
      status: {
        active: 'Активный',
        inactive: 'Неактивный',
        failed: 'Ошибка',
      },
      time: {
        never: 'Никогда',
        justNow: 'Только что',
        minutesAgo: '{count}м назад',
        hoursAgo: '{count}ч назад',
        daysAgo: '{count}д назад',
      },
      form: {
        basicInfo: 'Основная информация',
        urlLabel: 'URL *',
        urlReadOnly: 'URL (только чтение)',
        descriptionLabel: 'Описание',
        descriptionPlaceholder: 'Краткое описание источника данных',
        statusLabel: 'Статус',
        scrapingConfig: 'Настройки сбора данных',
        showAdvanced: 'Показать расширенные',
        hideAdvanced: 'Скрыть расширенные',
        parserType: 'Тип парсера',
        parserJsoup: 'JSoup (Статический HTML)',
        parserPlaywright: 'Playwright (JavaScript-рендеринг)',
        cssSelectors: 'CSS-селекторы',
        titleSelector: 'Селектор заголовка',
        descriptionSelector: 'Селектор описания',
        contentSelector: 'Селектор контента',
        cacheTtl: 'Время кэширования (часы)',
        cronSchedule: 'Расписание CRON',
        lastError: 'Последняя ошибка',
        retryCount: 'Количество повторов: {count}',
      },
      actions: {
        scrapeNow: 'Собрать сейчас',
        scraping: 'Сбор...',
        edit: 'Редактировать',
        delete: 'Удалить',
      },
      messages: {
        loadError: 'Ошибка загрузки источников данных',
        createSuccess: 'Источник данных успешно создан',
        createError: 'Ошибка создания источника данных',
        updateSuccess: 'Источник данных успешно обновлён',
        updateError: 'Ошибка обновления источника данных',
        deleteConfirm: 'Вы уверены, что хотите удалить источник данных "{url}"? Это также удалит весь собранный контент из этого источника.',
        deleteSuccess: 'Источник данных успешно удалён',
        deleteError: 'Ошибка удаления источника данных',
        scrapeStarted: 'Сбор данных запущен в фоновом режиме',
        scrapeSuccess: 'Сбор данных успешно завершён',
        scrapeError: 'Ошибка сбора данных: {message}',
        scrapeFailure: 'Ошибка запуска сбора данных',
        urlRequired: 'URL обязателен',
        urlInvalid: 'URL должен начинаться с http:// или https://',
      },
    },

    // POI Detail Page
    poi: {
      // Page header
      title: 'Точка интереса',
      loading: 'Загрузка информации о POI...',

      // Error states
      notFound: 'Точка интереса не найдена',
      notFoundMessage: 'POI с указанным идентификатором не существует или был удалён.',
      invalidId: 'Некорректный идентификатор POI',
      loadError: 'Ошибка загрузки информации о POI',

      // Field labels
      fields: {
        name: 'Название',
        description: 'Описание',
        type: 'Тип',
        region: 'Регион',
        coordinates: 'Координаты',
        latitude: 'Широта',
        longitude: 'Долгота',
        activities: 'Активности',
        metadata: 'Дополнительные данные',
        createdAt: 'Создано',
        updatedAt: 'Обновлено',
        createdByLabel: 'Кем создано',
        updatedByLabel: 'Кем обновлено',
        ipLabel: 'IP-адрес',
        unknownAuthor: '—',
        systemAuthor: 'Система',
      },

      // Change-history timeline
      history: {
        sectionTitle: 'История изменений',
        loading: 'Загрузка истории…',
        empty: 'Изменений пока нет',
        loadError: 'Не удалось загрузить историю изменений',
        created: 'Создано',
        updated: 'Изменено',
        noValue: '(пусто)',
        fieldLabels: {
          name: 'Название',
          latitude: 'Широта',
          longitude: 'Долгота',
          type: 'Тип',
          region: 'Регион',
          description: 'Описание',
          importanceScore: 'Важность',
          difficultyLevel: 'Сложность',
          familyFriendly: 'Для семьи',
          accessibilityLevel: 'Доступность',
          overnightPossible: 'Ночёвка возможна',
          avgVisitDurationMinutes: 'Ср. визит (мин)',
          activities: 'Активности',
          metadata: 'Дополнительные данные',
        },
      },

      // POI types (matching PoiType enum)
      types: {
        HOTEL: 'Отель',
        RESTAURANT: 'Ресторан',
        LANDMARK: 'Достопримечательность',
        MUSEUM: 'Музей',
        PARK: 'Парк',
        SHOPPING: 'Торговый центр',
        ENTERTAINMENT: 'Развлечения',
        TRANSPORT: 'Транспорт',
        CITY: 'Город',
        ATTRACTION: 'Достопримечательность',
        TRANSPORT_HUB: 'Транспортный узел',
        OTHER: 'Другое',
      },

      // Navigation
      backToScrapedContent: 'Вернуться к собранному контенту',
      backToAdmin: 'Вернуться в админ-панель',
      sourceContent: 'Исходный контент',
      viewSourceContent: 'Просмотреть исходный контент',

      // Empty/null states
      noDescription: 'Описание отсутствует',
      noActivities: 'Активности не указаны',
      noMetadata: 'Дополнительные данные отсутствуют',

      // Coordinate formatting
      coordinatesFormat: '{lat}° {latDir}, {lon}° {lonDir}',
      north: 'С',
      south: 'Ю',
      east: 'В',
      west: 'З',
    },

    // POI Management Panel (v2.1)
    poiManagement: {
      allPois: 'Все POI',
      unverified: 'Непроверенные',
      createPoi: 'Создать POI',
      clearFilters: 'Сбросить фильтры',
      edit: 'Редактировать',
      verify: 'Проверить',
      delete: 'Удалить',
      searchPlaceholder: 'Поиск по названию...',
      allTypes: 'Все типы',
      allStatuses: 'Все статусы',
      verifiedOnly: 'Проверенные',
      unverifiedOnly: 'Непроверенные',
      allSources: 'Все источники',
      sources: {
        manual: 'Вручную',
        smart: 'Умное создание',
        geocoded: 'Геокодирование',
        verificationCorrected: 'Исправлено верификацией',
        harvestLlm: 'Сбор (LLM)',
        territoryDiscovery: 'Обнаружение территории',
        scraping: 'Скрапинг',
        regionAnalysis: 'Анализ региона (ИИ)',
        unknown: 'Не указан',
      },
      confidence: 'Точность',
      source: 'Источник',
      actions: 'Действия',
      sortAsc: 'По возрастанию',
      sortDesc: 'По убыванию',
      loading: 'Загрузка\u2026',
      noPois: 'POI не найдены',
      noPoisHint: 'Попробуйте изменить параметры поиска или фильтров.',
      loadError: 'Не удалось загрузить POI. Проверьте подключение и попробуйте снова.',
      deleteConfirm: 'Удалить "{name}"? Это действие нельзя отменить.',
      deleteTitle: 'Удаление POI',
      deleteMessage: 'Удалить "{name}"? Это действие нельзя отменить.',
      deleting: 'Удаление\u2026',
      deleteError: 'Не удалось удалить POI. Попробуйте ещё раз.',
      createTitle: 'Создать POI',
      smartMode: 'Умное создание',
      manualMode: 'Вручную',
      smartDescription: '\u0412\u0432\u0435\u0434\u0438\u0442\u0435 \u043d\u0430\u0437\u0432\u0430\u043d\u0438\u0435 \u0438 \u0442\u0435\u0440\u0440\u0438\u0442\u043e\u0440\u0438\u044e. \u041a\u043e\u043e\u0440\u0434\u0438\u043d\u0430\u0442\u044b \u0438 \u043e\u043f\u0438\u0441\u0430\u043d\u0438\u0435 \u0431\u0443\u0434\u0443\u0442 \u0437\u0430\u043f\u043e\u043b\u043d\u0435\u043d\u044b \u0430\u0432\u0442\u043e\u043c\u0430\u0442\u0438\u0447\u0435\u0441\u043a\u0438. \u0412\u044b \u0441\u043c\u043e\u0436\u0435\u0442\u0435 \u0441\u043a\u043e\u0440\u0440\u0435\u043a\u0442\u0438\u0440\u043e\u0432\u0430\u0442\u044c \u0434\u0430\u043d\u043d\u044b\u0435 \u043f\u0435\u0440\u0435\u0434 \u0441\u043e\u0445\u0440\u0430\u043d\u0435\u043d\u0438\u0435\u043c.',
      namePlaceholder: 'напр. Эйфелева башня',
      territory: 'Территория',
      territoryPlaceholder: 'напр. Париж, Франция',
      cancel: 'Отмена',
      create: 'Создать',
      creating: 'Создание\u2026',
      createError: 'Не удалось создать POI',
      // Smart create review flow
      saveReview: '\u0421\u043e\u0445\u0440\u0430\u043d\u0438\u0442\u044c \u0438\u0437\u043c\u0435\u043d\u0435\u043d\u0438\u044f',
      cancelCreate: '\u0417\u0430\u043a\u0440\u044b\u0442\u044c',
      discardPoi: '\u041e\u0442\u043c\u0435\u043d\u0438\u0442\u044c POI',
      reviewTitle: '\u041f\u0440\u043e\u0432\u0435\u0440\u044c\u0442\u0435 \u0434\u0430\u043d\u043d\u044b\u0435',
      smartCreateSuccess: 'POI \u0441\u043e\u0437\u0434\u0430\u043d \u2014 \u043f\u0440\u043e\u0432\u0435\u0440\u044c\u0442\u0435 \u0434\u0430\u043d\u043d\u044b\u0435 \u043d\u0438\u0436\u0435',
      llmDegradationNote: '\u041e\u043f\u0438\u0441\u0430\u043d\u0438\u0435 \u043d\u0435 \u0443\u0434\u0430\u043b\u043e\u0441\u044c \u0441\u0433\u0435\u043d\u0435\u0440\u0438\u0440\u043e\u0432\u0430\u0442\u044c \u2014 \u0437\u0430\u043f\u043e\u043b\u043d\u0438\u0442\u0435 \u0432\u0440\u0443\u0447\u043d\u0443\u044e',
      geocodeError: '\u041d\u0435 \u0443\u0434\u0430\u043b\u043e\u0441\u044c \u043e\u043f\u0440\u0435\u0434\u0435\u043b\u0438\u0442\u044c \u043a\u043e\u043e\u0440\u0434\u0438\u043d\u0430\u0442\u044b. \u041f\u043e\u043f\u0440\u043e\u0431\u0443\u0439\u0442\u0435 \u0443\u0442\u043e\u0447\u043d\u0438\u0442\u044c \u043d\u0430\u0437\u0432\u0430\u043d\u0438\u0435 \u0438\u043b\u0438 \u043f\u0435\u0440\u0435\u043a\u043b\u044e\u0447\u0438\u0442\u0435\u0441\u044c \u043d\u0430 \u0440\u0443\u0447\u043d\u043e\u0439 \u0440\u0435\u0436\u0438\u043c.',
      territoryResolutionError: '\u041d\u0435 \u0443\u0434\u0430\u043b\u043e\u0441\u044c \u043e\u043f\u0440\u0435\u0434\u0435\u043b\u0438\u0442\u044c \u0440\u0435\u0433\u0438\u043e\u043d \u043f\u043e \u0443\u043a\u0430\u0437\u0430\u043d\u043d\u043e\u0439 \u0442\u0435\u0440\u0440\u0438\u0442\u043e\u0440\u0438\u0438. \u041f\u0440\u043e\u0432\u0435\u0440\u044c\u0442\u0435 \u043d\u0430\u043f\u0438\u0441\u0430\u043d\u0438\u0435 \u0438 \u043f\u043e\u043f\u0440\u043e\u0431\u0443\u0439\u0442\u0435 \u0441\u043d\u043e\u0432\u0430 \u2014 \u043c\u043e\u0436\u043d\u043e \u0443\u043a\u0430\u0437\u0430\u0442\u044c \u043d\u0430\u0437\u0432\u0430\u043d\u0438\u0435 \u0433\u043e\u0440\u043e\u0434\u0430, \u043e\u0431\u043b\u0430\u0441\u0442\u0438 \u0438\u043b\u0438 \u043a\u0440\u0430\u044f.',
      editTitle: 'Редактирование POI',
      save: 'Сохранить',
      saving: 'Сохранение\u2026',
      updateError: 'Не удалось обновить POI',
      duplicateNameError: 'POI с таким названием уже существует в этом регионе. Выберите другое название.',
      duplicateWarning: {
        title: 'Возможные дубликаты',
        subtitle: (count: number) => `Найдено ${count} похожих POI в радиусе 300 м`,
        candidateMeta: (distance: number, similarity: number) => `${distance} м • ${similarity}% сходство`,
        createAnyway: 'Создать всё равно',
        cancel: 'Отмена',
        creating: 'Создание…',
        ariaLabel: 'Предупреждение о дубликатах',
      },
      mapClickHint: 'Нажмите на карту для обновления координат',
      validation: {
        nameRequired: 'Название обязательно',
        latitudeRequired: 'Широта обязательна',
        longitudeRequired: 'Долгота обязательна',
        typeRequired: 'Тип обязателен',
        regionRequired: 'Регион обязателен',
        latitudeRange: 'Широта должна быть от -90 до 90',
        longitudeRange: 'Долгота должна быть от -180 до 180',
        latitudeInvalid: 'Введите числовое значение',
        longitudeInvalid: 'Введите числовое значение',
        duplicateName: 'POI с названием "{name}" уже существует в регионе {region}. Выберите другое название.',
      },
      // Detail panel (Phase 39)
      detail: 'Просмотр деталей',
      verificationHeading: 'Верификация',
      reverify: 'Перепроверить координаты',
      reverifying: 'Проверяем координаты\u2026',
      reverifyError: 'Ошибка верификации. Проверьте соединение и попробуйте снова.',
      reverifyProgress: 'Проверяем координаты через Nominatim (обратное геокодирование)\u2026',
      reverifyResultTitle: 'Результат верификации',
      reverifyStepRange: 'Проверка диапазона координат',
      reverifyStepNullIsland: 'Проверка Null Island',
      reverifyStepGeocode: 'Обратное геокодирование (Nominatim)',
      reverifyStepRegion: 'Нечёткое сравнение региона',
      reverifyPassed: 'Пройдено',
      reverifyFailed: 'Не пройдено',
      reverifyThreshold: 'Порог: \u2265 70% для верификации',
      reverifyDismiss: 'Закрыть',
      reverifyRefineWithTerritory: 'Ближайшая территория: «{place}», применим её для уточнения',
      reverifyRefining: 'Уточняем по территории\u2026',
      reverifyStepTerritoryRefinement: 'Уточнение по территории ({place})',
      reverifyStepCrossCheck: 'Перекрёстная проверка по названию',
      reverifyStepCrossCheckKm: 'км',
      reverifyStepCrossCheckCorrected: 'координаты скорректированы',
      reverifyManualHint: 'Геокодирование не удалось — проверьте координаты вручную или отредактируйте POI.',
      reverifyEditPoi: 'Редактировать POI',
      descriptionSuggestionTitle: 'Предложение описания от ИИ',
      descriptionSuggestionApply: 'Применить',
      descriptionSuggestionDismiss: 'Отклонить',
      descriptionSuggestionApplied: 'Описание обновлено',
      confidenceLabel: 'Уверенность',
      sourceLabel: 'Источник',
      attemptsLabel: 'Попыток',
      lastVerifiedLabel: 'Последняя проверка',
      lastVerifiedNever: 'Никогда',
      detailLoadError: 'Не удалось загрузить данные POI. Попробуйте снова.',
      infoButton: 'Просмотр деталей',
      // Stats bar (Phase 39)
      statsVerified: 'Проверено',
      statsUnverified: 'Не проверено',
      statsTotal: 'Всего',
      statsManual: 'Вручную',
      statsSmart: 'Умное создание',
      statsGeocoded: 'Геокодирование',
      statsError: 'Статистика недоступна',
      regionPlaceholder: 'Фильтр по региону...',
      showMap: 'Показать карту',
      hideMap: 'Скрыть карту',
      // Region analysis (TASK-47)
      regionAnalysis: {
        analyzeButton: 'Анализ региона',
        analyzing: 'Анализируем...',
        error: 'Не удалось проанализировать регион. Попробуйте снова.',
        title: 'Анализ региона',
        summary: 'Сводка',
        existingPois: 'существующих POI',
        byType: 'По типам',
        missingTypes: 'Недостающие типы',
        suggestions: 'Предложенные POI',
        noSuggestions: 'Предложения не найдены.',
        addPoi: 'Добавить POI',
        adding: 'Добавление...',
        added: 'Добавлено',
        addError: 'Не удалось добавить POI',
        geocodeFailed: 'Координаты не найдены',
        coordsPlaceholder: '55.7558, 37.6173',
        close: 'Закрыть',
        selectRegionHint: 'Выберите регион для анализа',
      },
      // Unified panel i18n (Phase 46)
      panelViewMode: 'Просмотр',
      panelEditMode: 'Редактирование',
      panelEdit: 'Редактировать',
      panelSave: 'Сохранить',
      panelCancelEdit: 'Отменить редактирование',
      panelCloseAriaLabel: 'Закрыть панель',
      panelSelectPrompt: 'Выберите объект в таблице',
      panelLoadError: 'Не удалось загрузить данные объекта. Попробуйте выбрать другой объект или обновите страницу.',
      panelCreateMode: 'Создание',
      panelCreateTitle: 'Новый POI',
      unsavedChangesTitle: 'Несохраненные изменения',
      unsavedChangesMessage: 'У вас есть несохраненные изменения. Отменить их или продолжить редактирование?',
      unsavedDiscard: 'Отменить',
      unsavedKeepEditing: 'Продолжить редактирование',
      panelLoadingSkeleton: 'Загрузка...',
      // Phase 74 — Smart Create enrichment review screen
      sectionSeasonality: 'Сезонность и доступность',
      sectionVisitInfo: 'Информация о посещении',
      llmUnverifiedBanner: 'Данные сгенерированы ИИ — проверьте перед сохранением',
      llmBadge: 'ИИ',
      notSpecified: 'Не указано',
      fieldSeasonalAvailability: 'Сезонная доступность',
      fieldBestSeason: 'Лучший сезон',
      fieldWeatherRisk: 'Погодный риск',
      fieldWinterAccess: 'Зимний доступ',
      fieldRoadAvailability: 'Состояние дороги',
      fieldAvgVisitTime: 'Среднее время посещения (мин)',
      fieldDifficultyLevel: 'Сложность',
      fieldFamilyFriendly: 'Подходит для семей',
      fieldAccessibilityLevel: 'Уровень доступности',
      fieldOvernightPossible: 'Возможен ночлег',
      fieldRecommendedStop: 'Рекомендуемая остановка',
      booleanYes: 'Да',
      booleanNo: 'Нет',
      // Seasonality enum display values
      seasonalAvailabilityYearRound: 'Круглогодично',
      seasonalAvailabilitySeasonal: 'Сезонно',
      bestSeasonSummer: 'Лето',
      bestSeasonWinter: 'Зима',
      bestSeasonSpring: 'Весна',
      bestSeasonAutumn: 'Осень',
      bestSeasonAll: 'Всесезонно',
      weatherRiskLow: 'Низкий',
      weatherRiskMedium: 'Средний',
      weatherRiskHigh: 'Высокий',
      winterAccessOpen: 'Открыт',
      winterAccessLimited: 'Ограничен',
      winterAccessClosed: 'Закрыт',
      roadAvailabilityPaved: 'Асфальт',
      roadAvailabilityDirt: 'Грунт',
      roadAvailabilityOffroad: 'Бездорожье',
      roadAvailabilityNa: 'Не применимо',
      roadAvailabilityAllYear: 'Круглый год',
      // Visit enum display values
      difficultyExtreme: 'Экстремальный',
      accessibilityFull: 'Полная',
      accessibilityPartial: 'Частичная',
      accessibilityLimited: 'Ограниченная',
      accessibilityNone: 'Отсутствует',
      // Phase 76 — Навигационные точки (геометрия)
      sectionNavigationPoints: 'Точки навигации',
      geometryToolbarLabel: 'Выберите тип, затем нажмите на карту',
      geometryTypePARKING: 'Парковка',
      geometryTypeENTRANCE: 'Вход',
      geometryTypeVIEWPOINT: 'Смотровая площадка',
      geometryTypeNAVIGATION: 'Навигация',
      geometryLabelPlaceholder: 'Метка (необязательно)',
      geometrySave: 'Сохранить точки навигации',
      geometrySaving: 'Сохранение…',
      geometryDeletePoint: 'Удалить навигационную точку',
      geometryEmpty: 'Нет точек навигации',
      geometryEmptyHint: 'Выберите тип выше, затем нажмите на карту для размещения точки',
      geometryMaxLimit: 'Достигнут максимум в 20 точек',
      geometrySaveSuccess: 'Точки навигации сохранены',
      geometrySaveError: 'Не удалось сохранить точки навигации. Проверьте соединение и попробуйте снова.',
      geometryCoordinates: 'Координаты',
      geometryLegendAriaLabel: 'Легенда карты',
      geometryDragHint: 'Перетащите маркеры для перемещения',
      // Phase 77 — Importance & Relevance section, verification signals, filters, columns
      sectionImportanceRelevance: 'Важность и релевантность',
      sectionVerificationSignals: 'Сигналы верификации',
      fieldImportanceScore: 'Оценка важности',
      fieldTravelRelevance: 'Туристическая релевантность',
      importanceMustSee: 'Обязательно',
      importanceNotable: 'Заметное',
      importanceSecondary: 'Второстепенное',
      importanceOptional: 'Необязательное',
      allImportance: 'Все уровни',
      signalOsmTag: 'OSM-тег tourism',
      signalWikimediaPhotos: 'Фото Wikimedia',
      signalReviewCount: 'Количество отзывов',
      signalRouteFrequency: 'Частота в маршрутах',
      signalNotChecked: 'Не проверено',
      columnToggle: 'Столбцы',
      columnImportance: 'Важность',
      columnSeason: 'Сезон',
      columnVisitDuration: 'Время посещения',
      filterSeason: 'Сезон',
      filterImportanceRange: 'Важность',
      filterFamilyFriendly: 'Семейность',
      filterAll: 'Все',
      filterImportanceHigh: 'Высокая (8-10)',
      filterImportanceMedium: 'Средняя (5-7)',
      filterImportanceLow: 'Низкая (1-4)',
      filterFamilyYes: 'Да',
      filterFamilyNo: 'Нет',
      allSeasons: 'Все сезоны',
      allFamilyFriendly: 'Семейность (все)',
      allDifficulty: 'Сложность (все)',
      filterDifficultyEasy: 'Лёгкий',
      filterDifficultyModerate: 'Средний',
      filterDifficultyHard: 'Сложный',
      duplicateWarningMiniMapAlt: 'Карта дублирующихся POI',
      notAvailable: '—',
      unitMinutes: 'мин',
      unitPhotos: 'фото',
    },

    // Auto-conversion job history
    conversionJobs: {
      tabTitle: 'Авто-конвертация',
      title: 'История авто-конвертации',
      loading: 'Загрузка истории задач...',
      error: 'Ошибка загрузки истории задач',
      empty: 'Задачи авто-конвертации ещё не запускались',
      retry: 'Повторить',
      // Summary card
      summary: {
        title: 'Последний запуск',
        noRuns: 'Задач ещё не было',
        lastRun: 'Последний запуск',
        status: 'Статус',
        processed: 'Обработано',
        succeeded: 'Успешно',
        failed: 'Ошибок',
        skipped: 'Пропущено',
      },
      // Table columns
      columns: {
        status: 'Статус',
        startTime: 'Начало',
        endTime: 'Окончание',
        duration: 'Длительность',
        processed: 'Обработано',
        succeeded: 'Успешно',
        failed: 'Ошибок',
        skipped: 'Пропущено',
        error: 'Ошибка',
      },
      // Status labels
      status: {
        RUNNING: 'Выполняется',
        SUCCESS: 'Успешно',
        FAILED: 'Ошибка',
      },
      // Duration formatting
      duration: {
        lessThanSecond: '< 1с',
        minutes: '{min}м {sec}с',
        seconds: '{sec}с',
      },
      // Pagination
      pagination: {
        previous: 'Назад',
        next: 'Далее',
        page: 'Страница {current} из {total}',
      },
    },

    // Duplicates management
    duplicates: {
      title: 'Обнаруженные дубликаты',
      empty: 'Нет обнаруженных дубликатов',
      loading: 'Загрузка дубликатов...',
      error: 'Ошибка загрузки дубликатов',
      retry: 'Повторить',
      scrapedContent: 'Собранный контент',
      existingPoi: 'Существующая точка',
      distance: 'Расстояние',
      similarity: 'Сходство названий',
      typeMatch: 'Совпадение типа',
      matchDetails: 'Детали совпадения',
      detectedAt: 'Обнаружено',
      actions: {
        forceConvert: 'Принудительно конвертировать',
        dismiss: 'Отклонить',
        merge: 'Объединить',
      },
      confirm: {
        forceConvertTitle: 'Подтверждение конвертации',
        forceConvertMessage: 'Будет создана отдельная точка интереса, несмотря на обнаруженное совпадение. Продолжить?',
        dismissTitle: 'Отклонить совпадение',
        dismissMessage: 'Отклонить совпадение? Контент останется в статусе «Пропущено».',
        mergeTitle: 'Объединить данные',
        mergeMessage: 'Выберите поля для обновления существующей точки данными из собранного контента:',
        mergeFieldName: 'Название',
        mergeFieldDescription: 'Описание',
        confirm: 'Подтвердить',
        cancel: 'Отмена',
      },
      success: {
        forceConverted: 'Контент успешно конвертирован',
        dismissed: 'Совпадение отклонено',
        merged: 'Данные успешно объединены',
      },
      yes: 'Да',
      no: 'Нет',
      distanceMeters: 'м',
      distanceKm: 'км',
    },
  },

  // Page-specific content
  pages: {
    routePlanner: {
      title: 'Планирование маршрута',
      subtitle: 'Создайте идеальный маршрут путешествия с помощью ИИ',
      loading: 'Создаем варианты маршрутов...',
    },
    planning: {
      heroTitle: 'Спланируйте идеальное путешествие',
      heroSubtitle: 'Расскажите, куда хотите отправиться, и ИИ подберёт лучшие маршруты для вас',
      loading: 'Ищем лучшие маршруты для вас...',
      newSearch: 'Начать новый поиск',
    },
    home: {
      heroTitle: 'Откройте своё идеальное путешествие',
      heroDescription: 'Позвольте ИИ помочь вам создать незабываемые впечатления от путешествий. От скрытых жемчужин до популярных направлений — мы создаём маршруты специально для вас.',
      startPlanning: 'Начать планирование',
      signInAndStart: 'Войти и начать',
      createAccount: 'Создать аккаунт',
      whyChoose: 'Почему выбирают TouristTools?',
      featureSmartPlanning: 'Умное планирование маршрутов',
      featureSmartPlanningDesc: 'ИИ анализирует тысячи направлений, чтобы создать идеальный маршрут на основе ваших предпочтений и стиля путешествий.',
      featureWorldwide: 'Направления по всему миру',
      featureWorldwideDesc: 'Исследуйте маршруты по разным регионам с местными особенностями, культурными достопримечательностями и скрытыми жемчужинами.',
      featureChatAssistant: 'Чат-ассистент',
      featureChatAssistantDesc: 'Получайте персональные рекомендации и ответы на ваши вопросы о путешествиях от нашего умного ассистента.',
      readyToStart: 'Готовы начать приключение?',
      readyToStartDesc: 'Присоединяйтесь к тысячам путешественников, которые планируют идеальные поездки с TouristTools.',
      planTrip: 'Спланировать поездку',
      start: 'Начать',
    },
    profile: {
      title: 'Профиль',
      comingSoon: 'Настройки профиля скоро будут доступны.',
      fio: 'ФИО',
      email: 'Email',
      language: 'Язык интерфейса',
      timezone: 'Часовой пояс',
      notSpecified: 'Не указано',
      editButton: 'Редактировать профиль',
      cancelButton: 'Отмена',
      saveButton: 'Сохранить изменения',
      savingButton: 'Сохранение...',
      form: {
        lastNameLabel: 'Фамилия *',
        firstNameLabel: 'Имя *',
        middleNameLabel: 'Отчество',
        emailLabel: 'Email *',
      },
      messages: {
        loadError: 'Ошибка загрузки профиля',
        saveSuccess: 'Профиль успешно обновлён',
        saveError: 'Ошибка при сохранении профиля',
        conflictError: 'Профиль был изменён в другой вкладке. Обновите страницу.',
        emailTaken: 'Email уже используется другим пользователем',
        validationError: 'Проверьте правильность введённых данных',
      },
      requiredNote: 'Поля отмеченные звёздочкой являются обязательными',
      personalDataTitle: 'Личные данные',
      nameLabel: 'Ваше имя',
      emailLabel: 'E-mail',
      emailVerified: 'E-mail подтверждён',
      emailNotVerified: 'E-mail не подтверждён',
      sidebar: {
        profileSection: 'Профиль',
        personalData: 'Личные данные',
        changePassword: 'Изменение пароля',
        myTrips: 'Мои путешествия',
        savedRoutes: 'Сохранённые маршруты',
        archive: 'Архив поездок',
        messages: 'Сообщения',
        comingSoon: 'Скоро',
        logout: 'Выйти',
      },
      passwordCard: {
        title: 'Изменение пароля',
        currentPassword: 'Текущий пароль',
        newPassword: 'Новый пароль',
        confirmPassword: 'Повторите пароль',
        save: 'Сохранить',
        saving: 'Сохранение...',
        success: 'Пароль успешно изменён',
        wrongCurrent: 'Неверный текущий пароль',
        weakPassword: 'Пароль не соответствует требованиям безопасности',
        genericError: 'Не удалось изменить пароль',
      },
    },
    // ============================================
    // CHAT LANDING (v2.0 Chat-Style Entry Point)
    // ============================================
    chatLanding: {
      greetingPersonal: 'ПРИВЕТ, {name}!',
      greetingGeneric: 'ПРИВЕТ!',
      heroHeadline: 'Куда отправимся в путешествие?',
      heroDescription: 'Опишите желаемый маршрут — регион, сложность, интересы. ИИ подберёт готовые маршруты, которые можно сравнить, объединить или изменить по вашим предпочтениям',
      placeholder: 'Например, Алтай на 5 дней на машине',
      micLabel: 'Голосовой ввод (скоро)',
      send: 'Отправить',
      chip1: 'Откройте для себя: Краснодарский край',
      chip2: 'Исследуйте регион: Калининградская область',
      chip3: 'Отправьтесь в путешествие: Республика Карелия',
      thinkingStatus1: 'Анализируем предпочтения...',
      thinkingStatus2: 'Ищем маршруты...',
      thinkingStatus3: 'Почти готово...',
      cancel: 'Отмена',
      queryError: 'Не удалось обработать запрос. Попробуйте ещё раз.',
      returnToResults: 'Вернуться к результатам',
      dismissBanner: 'Закрыть',
      // Route results display (Phase 34)
      routeResults: {
        tabAll: 'Все',
        tabWalk: 'Пешком',
        tabCar: 'Авто',
        yourQuery: 'Ваш запрос',
        editQuery: 'Изменить',
        extractedParams: 'Параметры поиска',
        noResults: 'Маршруты не найдены',
        noResultsHint: 'Попробуйте изменить запрос или выбрать другой регион',
        distance: 'км',
        day1: 'день',
        day2_4: 'дня',
        day5: 'дней',
        difficultyEasy: 'Лёгкий',
        difficultyMedium: 'Средний',
        difficultyHard: 'Сложный',
        walkLabel: 'Пешком',
        carLabel: 'Авто',
        waypointsMore: 'ещё +{count}',
        suggestionBanner: 'Подсказка',
        resultsCount: 'Найдено маршрутов: {count}',
        paramRegion: 'Регион',
        paramDifficulty: 'Сложность',
        paramDuration: 'Время',
        paramInterests: 'Интересы',
        backToResults: '← К результатам поиска',
        openRoute: 'Подробнее о маршруте',
        compareButton: 'Сравнить',
        errorTitle: 'Не удалось загрузить маршруты',
        errorHint: 'Проверьте соединение и попробуйте снова',
        retry: 'Повторить поиск',
      },
    },
  },

  // Saved routes
  savedRoutes: {
    title: 'Сохранённые маршруты',
    empty: 'У вас пока нет сохранённых маршрутов',
    loading: 'Загрузка сохранённых маршрутов...',
    selectForComparison: 'Выбрать {name} для сравнения',
    waypointCount: 'точек',
    daysCount: 'дней',
    savedAt: 'Сохранён',
    waypointsLabel: 'Точек',
    daysLabel: 'Дней',
    share: 'Поделиться',
    transport: {
      car: 'Авто',
      walking: 'Пеший',
      combined: 'Авто + пеший',
      city: 'Городской',
      active: 'Активный',
      family: 'Семейный',
    },
    confirmDelete: 'Вы уверены, что хотите удалить этот маршрут?',
    deleteSuccess: 'Маршрут удалён',
    deleteFailed: 'Не удалось удалить маршрут',
    openRoute: 'Открыть маршрут',
    deleteRoute: 'Удалить',
    saveButton: 'Сохранить маршрут',
    updateButton: 'Сохранить изменения',
    modal: {
      title: 'Сохранить маршрут',
      nameLabel: 'Название маршрута',
      namePlaceholder: 'Например: Поездка по Золотому кольцу',
      save: 'Сохранить',
      saving: 'Сохранение...',
      cancel: 'Отмена',
    },
    success: {
      saved: 'Маршрут успешно сохранён',
      updated: 'Маршрут успешно обновлён',
    },
    errors: {
      nameRequired: 'Введите название маршрута',
      duplicateName: 'Маршрут с таким названием уже существует',
      saveFailed: 'Не удалось сохранить маршрут',
      loadFailed: 'Не удалось загрузить сохранённые маршруты',
    },
  },

  // Favorites
  favorites: {
    title: 'Избранное',
    tabs: {
      routes: 'Маршруты',
      pois: 'Достопримечательности',
    },
    empty: {
      routes: 'У вас пока нет избранных маршрутов',
      pois: 'У вас пока нет избранных достопримечательностей',
    },
    loading: 'Загрузка избранного...',
    addToFavorites: 'Добавить в избранное',
    removeFromFavorites: 'Убрать из избранного',
    toggleFailed: 'Не удалось обновить избранное',
    errors: {
      loadFailed: 'Не удалось загрузить избранное',
    },
  },

  // Route comparison
  comparison: {
    title: 'Сравнение маршрутов',
    compareRoutes: 'Сравнить маршруты',
    selectAtLeastTwo: 'Выберите минимум 2 маршрута для сравнения',
    maxThreeRoutes: 'Можно сравнить максимум 3 маршрута',
    analyzingRoutes: 'Анализируем маршруты с помощью ИИ...',
    analysisError: 'Не удалось получить анализ маршрутов',
    llmAnalysis: 'Анализ ИИ',
    keyDifferences: 'Ключевые различия',
    tradeoffs: 'Компромиссы',
    recommendation: 'Рекомендация',
    refreshWarning: 'Данные сравнения были потеряны. Выберите маршруты снова.',
    backToSavedRoutes: 'Назад к сохранённым маршрутам',
    backToPlanning: 'Назад к планированию',
    routeLabel: 'Маршрут',
    transport: 'Транспорт',
    duration: 'Длительность',
    cost: 'Стоимость',
    waypoints: 'Точки маршрута',
    priceNotSet: 'Не указана',
    selected: 'Выбрано',
    someRoutesDeleted: 'Некоторые выбранные маршруты были удалены',
    comparing: 'Сравниваются',
    criteriaHeader: 'Критерий оценки',
    pointsLabel: 'Количество точек',
    pace: 'Темп',
    paceDense: 'Плотный',
    paceMedium: 'Средний',
    paceCalm: 'Спокойный',
    share: 'Поделиться',
    favorite: 'В избранное',
  },

  // Pluralization forms for common words
  plurals: {
    variant: ['вариант', 'варианта', 'вариантов'] as [string, string, string],
    day: ['день', 'дня', 'дней'] as [string, string, string],
    person: ['человек', 'человека', 'человек'] as [string, string, string],
    route: ['маршрут', 'маршрута', 'маршрутов'] as [string, string, string],
    place: ['место', 'места', 'мест'] as [string, string, string],
  },

  // ============================================
  // GPX ADMIN (Walk Route Import - v1.3)
  // ============================================
  gpxAdmin: {
    // Territory filter dropdown
    allTerritories: 'Все территории',

    // Bulk delete button label (interpolate {count} with formatMessage)
    deleteSelected: 'Удалить выбранные ({count})',

    // Confirmation dialog for bulk delete (interpolate {count})
    confirmBulkDelete: 'Удалить {count} маршрутов?',

    // Sort direction indicator labels (for aria-labels if needed)
    sortAsc: 'Сортировка по возрастанию',
    sortDesc: 'Сортировка по убыванию',

    // Overlap detection
    detectOverlaps: 'Обнаружить пересечения',
    detecting: 'Обнаружение...',
    overlapsTitle: 'Пересечения маршрутов',
    noOverlapsFound: 'Пересечений не найдено',
    hideOverlaps: 'Скрыть',
    routeA: 'Маршрут A',
    routeB: 'Маршрут B',
    crossingPoint: 'Точка пересечения',
    detectedAt: 'Обнаружено',
    selectTerritoryFirst: 'Выберите территорию',
    // Region-based territory dropdown (v2.4)
    allRegions: 'Все регионы',
    regionsLoading: 'Загрузка регионов...',
    regionsLoadError: 'Не удалось загрузить регионы',
    noRegionBadge: 'Регион не определён',
    noRegionTooltip: 'Территория не была сопоставлена при миграции. Отредактируйте маршрут для назначения региона.',
    noRegionEditOption: '— Без региона —',
    emptyRegion: 'Нет маршрутов для выбранного региона.',
    selectRegionFirst: 'Выберите регион',
    // OSRM import
    osrmImport: 'Импорт OSRM маршрута',
    osrmUrlPlaceholder: 'Вставьте URL маршрута (GPX trace или OSRM направления)',
    osrmUrlHint: 'Поддерживаются OSM trace URLs и OSRM directions URLs',
    mapPicker: 'Выбор на карте',
    urlPaste: 'Вставить URL',
    preview: 'Предпросмотр',
    previewLoading: 'Загрузка предпросмотра...',
    saveRoute: 'Сохранить маршрут',
    saving: 'Сохранение...',
    startPoint: 'Начальная точка',
    endPoint: 'Конечная точка',
    clickMapStart: 'Кликните на карте для выбора начальной точки',
    clickMapEnd: 'Кликните на карте для выбора конечной точки',
    poiCount: 'Точек интереса рядом',
    importSuccess: 'Маршрут успешно импортирован',
    importFailed: 'Ошибка импорта маршрута',
    osrmOrigin: 'OSRM',
    osmTraceOrigin: 'GPX Trace',

    // Page
    pageTitle: 'Импорт GPX маршрутов',
    pollingIndicator: 'Обновление данных...',

    // Import form
    importTitle: 'Импорт GPX маршрута',
    importHint: 'Вставьте URL трека OpenStreetMap или URL маршрута OSRM для импорта пешего маршрута.',
    importPlaceholder: 'https://www.openstreetmap.org/trace/12345 или URL маршрута',
    importButton: 'Импорт',
    importing: 'Импорт...',
    importStarted: 'Импорт начат — отслеживайте статус ниже.',
    importError: 'Не удалось начать импорт. Попробуйте ещё раз.',
    urlFormatError: 'Поддерживаются: OSM trace URLs и OSRM directions URLs',
    snapDistanceLabel: 'Макс. расстояние привязки к дороге:',
    snapDistanceUnit: 'м',

    // Table headers
    headerName: 'Имя',
    headerTerritory: 'Территория',
    headerStatus: 'Статус',
    headerDescription: 'Описание',
    headerPoints: 'Точки',
    headerDistance: 'Расстояние (км)',
    headerOsmLink: 'Ссылка OSM',
    headerActions: 'Действия',
    headerCreated: 'Создано',

    // Import status labels
    statusPending: 'Ожидание',
    statusProcessing: 'Обработка',
    statusCompleted: 'Завершено',
    statusFailed: 'Ошибка',
    statusMerged: 'Объединено',

    // Description status labels
    descPending: 'Ожидание',
    descGenerating: 'Генерация...',
    descReady: 'Готово',
    descFailed: 'Ошибка',

    // Action buttons
    edit: 'Редактировать',
    save: 'Сохранить',
    cancel: 'Отмена',
    delete: 'Удалить',
    regenerate: 'Регенерация',
    regenerating: 'Генерация...',
    namePlaceholder: 'Название маршрута',
    territoryPlaceholder: 'Территория',

    // Route labels
    unnamedRoute: 'Без названия',

    // Table empty/loading states
    loading: 'Загрузка маршрутов...',
    emptyState: 'Маршруты ещё не импортированы. Вставьте URL OSM трека выше, чтобы начать.',
    emptyTerritory: 'Нет маршрутов для выбранной территории.',
    selectAll: 'Выбрать все видимые маршруты',

    // Toast messages
    routeDeleted: 'Маршрут удалён.',
    deleteFailed: 'Не удалось удалить маршрут.',
    saveFailed: 'Не удалось сохранить изменения.',
    regenStarted: 'Регенерация описания запущена.',
    regenFailed: 'Не удалось запустить регенерацию описания.',
    bulkDeleted: 'Удалено {count} маршрутов.',
    bulkDeleteFailed: 'Ошибка при удалении некоторых маршрутов.',

    // Confirm dialogs
    confirmDelete: 'Удалить маршрут "{name}"? Это действие нельзя отменить.',

    // Imported routes section
    importedRoutesTitle: 'Импортированные маршруты',
    territoryLabel: 'Территория:',

    // Overlap detection errors
    overlapFetchFailed: 'Не удалось получить результаты обнаружения пересечений.',
    overlapDetectFailed: 'Не удалось запустить обнаружение пересечений.',

    // Aria labels for accessibility
    ariaOsmUrl: 'URL маршрута OSM',
    ariaRegenerate: 'Пересоздать описание',
    ariaEdit: 'Редактировать маршрут',
    ariaDelete: 'Удалить маршрут',
  },

  // ============================================
  // HARVEST (Web POI Harvester - v1.2)
  // ============================================
  harvest: {
    tabName: 'Сбор данных',
    title: 'Задания сбора данных',
    createJob: 'Новое задание',
    territory: 'Территория',
    territoryType: 'Тип территории',
    parentRegion: 'Родительский регион',
    urls: 'URL источников',
    notes: 'Заметки',
    startJob: 'Запустить',
    retryJob: 'Повторить',
    deleteJob: 'Удалить',
    exportCsv: 'Экспорт CSV',
    exportJson: 'Экспорт JSON',

    status: {
      PENDING: 'Ожидает',
      SCRAPING: 'Сбор данных',
      AWAITING_ADMIN: 'Требует ввода',
      PROCESSING: 'Обработка',
      COMPLETED: 'Завершено',
      FAILED: 'Ошибка',
    },

    urlStatus: {
      QUEUED: 'В очереди',
      SCRAPING: 'Сбор',
      SCRAPED: 'Собрано',
      FAILED: 'Ошибка',
      ADMIN_PROVIDED: 'Предоставлено',
      SKIPPED: 'Пропущено',
      PROCESSING: 'Обработка',
      PROCESSED: 'Обработано',
    },

    stats: {
      totalUrls: 'Всего URL',
      processed: 'Обработано',
      failed: 'Ошибки',
      skipped: 'Пропущено',
      poiCount: 'POI создано',
      successRate: 'Успешность',
      tokensUsed: 'Токены',
      processingTime: 'Время обработки',
      timeUnits: {
        ms: 'мс',
        sec: 'сек',
        min: 'мин',
      },
      poiFound: 'POI найдено',
    },

    list: {
      urlColumn: 'URL',
      urlMore: '+{count} ещё',
      loadingUrls: 'Загрузка URL...',
      urlLoadError: 'Ошибка загрузки URL',
      cancel: 'Отмена',
      newJob: '+ Новое задание',
      newJobTitle: 'Новое задание сбора',
      byUrl: 'По URL',
      pasteText: 'Вставить текст',
      searchTerritory: 'Поиск по территории',
      urlLabel: 'URL (по одному на строку):',
      urlPlaceholder: 'https://example.com/page1\nhttps://example.com/page2',
      articleText: 'Текст статьи:',
      articlePlaceholder: 'Вставьте текст туристической статьи для извлечения POI...',
      discoveryInfo: 'GigaChat автоматически составит список достопримечательностей для указанной территории. Укажите название территории и тип ниже.',
      territoryLabel: 'Территория:',
      territoryPlaceholder: 'Например: Дагестан',
      typeLabel: 'Тип:',
      parentRegionLabel: 'Родительский регион:',
      parentRegionPlaceholder: 'Например: Россия',
      notesLabel: 'Заметки:',
      notesPlaceholder: 'Необязательные заметки',
      creating: 'Создание...',
      createAndStart: 'Создать и запустить',
      loading: 'Загрузка...',
      emptyTitle: 'Заданий сбора данных пока нет',
      emptyHint: 'Создайте новое задание, указав URL источников',
      headerTerritory: 'Территория',
      headerMode: 'Режим',
      headerStatus: 'Статус',
      headerSources: 'Источники',
      headerPoi: 'POI',
      headerCreated: 'Создано',
      headerActions: 'Действия',
      moreUrls: '+{count} ещё',
      sourceText: 'текст',
      sourceAuto: 'авто',
      failedCount: '{count} ош.',
      inputBtn: 'Ввод',
      retryBtn: 'Повторить',
      deleteBtn: 'Удалить',
      prevPage: '← Назад',
      pageInfo: 'Страница {page} из {total}',
      nextPage: 'Далее →',
    },

    // Mode labels
    mode: {
      URL_SCRAPING: 'URL',
      TEXT_INPUT: 'Текст',
      TERRITORY_DISCOVERY: 'Территория',
    },

    // POI type labels
    poiTypes: {
      HOTEL: 'Отель',
      RESTAURANT: 'Ресторан',
      LANDMARK: 'Достопримечательность',
      MUSEUM: 'Музей',
      PARK: 'Парк',
      SHOPPING: 'Торговый центр',
      ENTERTAINMENT: 'Развлечения',
      TRANSPORT: 'Транспорт',
      CITY: 'Город',
      ATTRACTION: 'Достопримечательность',
      TRANSPORT_HUB: 'Транспортный узел',
      OTHER: 'Другое',
    },

    // Content source labels
    contentSource: {
      ADMIN_PASTE: 'Ручной ввод',
      ADMIN_UPLOAD: 'Загрузка файла',
      SCRAPER: 'Автосбор',
    },

    // Territory types
    territoryTypes: {
      CITY: 'Город',
      REGION: 'Регион',
      COUNTRY: 'Страна',
      DISTRICT: 'Район',
      ISLAND: 'Остров',
      NATIONAL_PARK: 'Нац. парк',
      RESORT: 'Курорт',
    },

    // Toast messages
    messages: {
      loadError: 'Ошибка загрузки заданий',
      createError: 'Ошибка создания задания',
      createSuccess: 'Задание создано: {mode}',
      jobStarted: 'Задание запущено',
      retrySuccess: 'Задание перезапущено',
      retryError: 'Ошибка перезапуска',
      deleteConfirm: 'Удалить задание?',
      deleteSuccess: 'Задание удалено',
      deleteError: 'Ошибка удаления',
      updateSuccess: 'Задание обновлено',
      updateError: 'Ошибка сохранения',
      exportSuccess: 'Экспорт {format} завершён',
      exportError: 'Ошибка экспорта',
      poiMapError: 'Ошибка загрузки карты POI',
      territoryRequired: 'Укажите название территории',
      urlRequired: 'Укажите хотя бы один URL',
      urlInvalidFormat: 'Все URL должны начинаться с http:// или https://',
      textRequired: 'Вставьте текст для анализа',
    },

    // Detail page strings
    detail: {
      loadError: 'Ошибка загрузки задания',
      notFound: 'Задание не найдено',
      backToList: '← К списку заданий',
      jobTitle: 'Задание #{id}: {name}',
      typeLabel: 'Тип:',
      regionLabel: 'Регион:',
      createdLabel: 'Создано:',
      authorLabel: 'Автор:',
      languageLabel: 'Язык:',
      regionPlaceholder: 'Регион (необязательно)',
      awaitingAlert: 'Некоторые URL не удалось собрать. Перейдите к вводу контента.',
      goToInput: 'Перейти к вводу',
      goToContentInput: 'Перейти к вводу контента',
      saving: 'Сохранение...',
      save: 'Сохранить',
      cancel: 'Отмена',
      edit: 'Редактировать',
      starting: 'Запуск...',
      start: 'Запустить',
      retry: 'Повторить',
      delete: 'Удалить',
      poiMapTitle: 'Карта POI ({count})',
      loadingMap: 'Загрузка карты...',
      poiListTitle: 'Список POI ({count})',
      poiTableNum: '#',
      poiTableName: 'Название',
      poiTableType: 'Тип',
      poiTableCoords: 'Координаты',
      progressScraped: 'Собрано:',
      progressFailed: 'Ошибки:',
      progressSkipped: 'Пропущено:',
      urlSectionTitle: 'URL ({count})',
      urlTableNum: '#',
      urlTableUrl: 'URL',
      urlTableStatus: 'Статус',
      urlTableSource: 'Источник',
      urlTablePoi: 'POI',
      urlTableError: 'Ошибка',
      discoveryNote: '+ {count} POI найдено через автоматический поиск по территории',
      addUrlsTitle: 'Добавить URL',
      addUrlsPlaceholder: 'Введите URL (по одному на строку)',
      notesTitle: 'Заметки',
      errorTitle: 'Ошибка задания',
    },

    contentInput: {
      title: 'Ввод контента',
      failedUrls: 'Проблемные URL',
      pasteLabel: 'Вставьте текст контента:',
      uploadLabel: 'Или загрузите файл (HTML, TXT, PDF):',
      preview: 'Предпросмотр',
      submit: 'Отправить контент',
      skip: 'Пропустить',
      submitting: 'Отправка...',
      back: 'Назад',
      urlsNeedInput: 'URL требуют ввода',
      contentFor: 'Контент для:',
      placeholder: 'Вставьте HTML или текстовый контент страницы...',
      chars: 'символов',
      loadError: 'Ошибка загрузки задания',
      submitSuccess: 'Контент предоставлен',
      submitError: 'Ошибка при отправке контента',
      uploadSuccess: 'Файл загружен',
      uploadError: 'Ошибка при загрузке файла',
      tooShort: 'Минимальная длина текста — 100 символов',
      skipSuccess: 'URL пропущен',
      skipError: 'Ошибка при пропуске URL',
      noSelection: 'Выберите URL для ввода контента',
      notFound: 'Задание не найдено',
      loading: 'Загрузка...',
      allResolved: 'Все URL обработаны! Задание продолжает выполнение.',
    },
  },
  booking: {
    bookThisRoute: 'Забронировать маршрут',
    stepper: {
      dates: 'Даты',
      guides: 'Гиды',
      confirmation: 'Подтверждение',
      payment: 'Оплата',
    },
    guide: {
      addToRoute: 'Добавить к маршруту',
      details: 'Подробнее',
      skip: 'Пропустить',
      backToGuides: '← Назад к гидам',
    },
    payment: {
      payForExcursion: 'Оплатить экскурсию',
      remainderNote: 'Остаток оплачивается в день экскурсии',
      timeRemaining: 'Осталось времени для оплаты:',
      retryPayment: 'Повторить оплату',
      attemptCounter: 'Попытка {n} из 2',
      errorRetry: 'Оплата не прошла. Попробуйте ещё раз.',
      errorFinal: 'Оплата не прошла. Пожалуйста, выберите другого гида.',
      successHeading: 'Оплата прошла успешно!',
      successBody: 'Предоплата экскурсии оплачена. До встречи {date}!',
    },
    confirmation: {
      awaitingGuide: 'Ожидаем подтверждения гида',
      awaitingBody: 'Гид получил запрос на экскурсию. Обычно это занимает несколько часов.',
      cancelRequest: 'Отменить запрос',
      rejectedBanner: 'Гид {name} отклонил запрос. Вот другие варианты.',
      expiredBanner: 'Время ожидания истекло. Вот другие варианты.',
    },
    transport: {
      heading: 'Транспорт и отели',
      comingSoon: 'Скоро появится',
    },
    notifications: {
      ariaLabel: 'Уведомления',
      empty: 'Нет новых уведомлений',
      markAllRead: 'Отметить все как прочитанные',
      viewBooking: 'Перейти к бронированию',
      title: 'Уведомления',
    },
    calendar: {
      prevMonth: 'Предыдущий месяц',
      nextMonth: 'Следующий месяц',
    },
    empty: {
      noGuides: 'Нет доступных гидов на выбранные даты',
      noGuidesBody: 'Попробуйте изменить даты поездки',
      noGuidesInCity: 'Нет доступных гидов на выбранные даты в {city}. Попробуйте другие даты или продолжите без гида.',
      changeDates: 'Изменить даты',
      skipGuide: 'Без гида',
    },
    error: {
      loadFailed: 'Не удалось загрузить данные. Попробуйте обновить страницу.',
      invalidTransition: 'Действие недоступно для текущего статуса заявки.',
    },
    guideBookings: {
      title: 'Заявки на экскурсии',
      subtitle: 'Новые запросы от туристов',
      confirm: 'Подтвердить',
      reject: 'Отклонить',
      empty: 'Нет новых заявок',
      emptyBody: 'Здесь появятся запросы от туристов',
      tourist: 'Турист',
      route: 'Маршрут',
      dates: 'Даты',
      excursionType: 'Тип экскурсии',
      price: 'Стоимость',
      actionError: 'Не удалось выполнить действие. Попробуйте ещё раз.',
    },
    admin: {
      emptyGuides: 'Гиды не добавлены',
      emptyGuidesBody: 'Нажмите «Добавить гида» чтобы создать первый профиль',
      addGuide: 'Добавить гида',
      saveGuide: 'Сохранить гида',
      discardChanges: 'Отменить изменения',
    },
    dateSelection: {
      startDate: 'Дата начала',
      endDate: 'Дата окончания',
      selectDates: 'Выберите даты поездки',
      next: 'Далее',
      mismatchWarning: 'Маршрут рассчитан на {routeDays} дн., но вы выбрали {selectedDays} дн. Хотите перераспределить маршрут?',
      confirmReoptimize: 'Перераспределить',
      cancelReoptimize: 'Отмена',
      fixDates: 'Исправить',
      startPoint: 'Начало маршрута:',
    },
  },

  persistentSearch: {
    placeholder: 'Введите направление...',
    submit: 'Найти маршруты',
    validationEmpty: 'Введите направление для поиска',
    updating: 'Обновляем маршруты...',
    ariaLabel: 'Поисковый запрос',
  },

  optimize: {
    button: 'Оптимизировать с ИИ',
    optimizing: 'Оптимизация...',
    bannerMessage: 'Маршрут оптимизирован',
    accept: 'Принять',
    revert: 'Отменить',
    descriptionWarning: 'Описание не удалось обновить -- отредактируйте вручную',
    tooFewWaypoints: 'Нужно минимум 3 точки для оптимизации',
  },

  routeDetail: {
    datesPanel: {
      heading: 'Даты поездки',
      validationError: 'Не удалось проверить даты. Попробуйте ещё раз.',
      mismatchUnsaved: 'Маршрут рассчитан на {routeDays} дн., но вы выбрали {selectedDays} дн. Подтвердите выбор.',
      confirmUnsaved: 'Подтвердить',
    },
    actionButtons: {
      goWithGuide: 'Поехать с гидом',
      goSolo: 'Поехать',
      tooltipNoDates: 'Сначала укажите даты',
      tooltipMismatch: 'Подтвердите даты',
      tooltipValidating: 'Проверка дат...',
      saving: 'Сохранение...',
      autoSaveError: 'Не удалось сохранить маршрут. Попробуйте ещё раз.',
      autoSaveName: 'Маршрут по {region} — {date}',
    },
    distanceUnit: 'км',
    share: {
      ariaLabel: 'Поделиться маршрутом',
      copied: 'Скопировано!',
    },
    waypoints: {
      expand: 'Развернуть',
      collapse: 'Свернуть',
      empty: 'Список точек пуст',
    },
    calendar: {
      selectStart: 'Выберите дату начала',
    },
  },

  // ============================================
  // GROUP BOOKING (Phase 66)
  // ============================================
  group: {
    formingStatus: 'Ожидаем группу: {x} из {n} участников, дедлайн {date}',
    filledStatus: 'Группа набрана! Переходите к оплате.',
    cancelledHeading: 'Группа не набралась',
    cancelledBody: 'Оплата возвращена в течение 3-5 рабочих дней.',
    soloFallbackHeading: 'Хотите продолжить без группы?',
    soloFallbackBody: 'Гид предлагает индивидуальную экскурсию за ₽{price}.',
    soloFallbackCta: 'Продолжить без группы',
    soloFallbackConfirm: 'Подтвердить — ₽{price}',
    soloFallbackCancel: 'Отмена',
    soloFallbackHelper: 'Вы перейдёте на индивидуальную цену ₽{price}.',
  },

  // ============================================
  // MONETIZATION (Phase 66)
  // ============================================
  monetization: {
    unlockCta: 'Разблокировать маршрут',
    payUnlock: 'Оплатить — ₽{amount}',
    donateCta: 'Задонатить ₽{amount}',
    freeCtaSave: 'Взять маршрут',
    freeCtaSaved: 'Маршрут сохранён',
    paywallHeading: 'Полный маршрут доступен после оплаты',
    paywallBody: 'Вы видите первые 2 точки. Оплатите один раз, чтобы открыть все {N} точек.',
    unlockSuccess: 'Маршрут открыт! Теперь вам доступны все точки.',
    donationSuccess: 'Спасибо за поддержку автора!',
    donationPlaceholder: 'Другая сумма',
    donationMinError: 'Введите сумму от 10 ₽',
    paymentErrorRetry: 'Оплата не прошла. Попробуйте ещё раз.',
    paymentErrorFinal: 'Не удалось провести оплату. Обратитесь в поддержку.',
  },

  // ============================================
  // EARNINGS (Phase 66 - Guide Cabinet)
  // ============================================
  earnings: {
    heading: 'Доходы',
    thisMonth: 'Этот месяц',
    lastMonth: 'Прошлый месяц',
    allTime: 'Всё время',
    unlockLabel: 'Разблокировок',
    donationLabel: 'Донаций',
    empty: 'Пока нет данных за этот период',
  },

  // ============================================
  // GUIDE GROUP BOOKINGS (Phase 66 - Guide Cabinet)
  // ============================================
  guideGroupBookings: {
    heading: 'Групповые бронирования',
    fillStatus: '{current} из {min} участников',
    deadline: 'Дедлайн: {date}',
    empty: 'Нет активных групповых бронирований',
  },

  // ============================================
  // BRAND (Phase 80 — Shared UI Components)
  // ============================================
  brand: {
    logoAlt: 'Мой маршрут — AI Travel Planner',
    heroImageAlt: 'Пейзаж путешествия',
    userIconAlt: 'Профиль пользователя',
  },

  // ============================================
  // ROUTE UNITS (Phase 80 — PillBadge units)
  // ============================================
  route: {
    unitKm: 'км',
    unitHours: 'ч',
    unitMeters: 'м',
  },

  // Phase 76–78 — Публичная страница POI
  poiDetail: {
    sectionNavigationPoints: 'Точки навигации',
    // Phase 78 — разделы обогащения
    sectionSeasonality: 'Сезонность и доступность',
    sectionVisitInfo: 'Информация о визите',
    fieldSeasonalAvailability: 'Сезонная доступность',
    fieldBestSeason: 'Лучший сезон',
    fieldWeatherRisk: 'Погодные риски',
    fieldWinterAccess: 'Зимний доступ',
    fieldRoadAvailability: 'Дорожная доступность',
    fieldDuration: 'Типичный визит',
    fieldDifficulty: 'Сложность',
    fieldFamilyFriendly: 'Подходит для семей',
    fieldAccessibility: 'Уровень доступности',
    fieldOvernightPossible: 'Ночёвка возможна',
    durationMinutes: '{n} мин',
    durationHours: '{h} ч {m} мин',
    importanceMustSee: 'Обязательно к посещению',
    importanceNotable: 'Примечательное',
    importanceSecondary: 'Второстепенное',
    importanceOptional: 'Опциональное',
  },
};

// Type for translation keys (auto-inferred)
export type TranslationKeys = typeof RU;

/**
 * Helper function to replace placeholders in a string.
 * Example: formatMessage("Hello {name}", { name: "World" }) => "Hello World"
 */
export function formatMessage(
  template: string,
  params: Record<string, string | number>
): string {
  return Object.entries(params).reduce(
    (result, [key, value]) => result.replace(`{${key}}`, String(value)),
    template
  );
}

/**
 * Russian pluralization helper.
 * @param count - The number to pluralize
 * @param forms - Array of 3 forms: [1, 2-4, 5+]
 * @example pluralize(1, ['день', 'дня', 'дней']) => 'день'
 * @example pluralize(3, ['день', 'дня', 'дней']) => 'дня'
 * @example pluralize(5, ['день', 'дня', 'дней']) => 'дней'
 */
export function pluralizeEn(count: number, forms: [string, string, string]): string {
  return Math.abs(count) === 1 ? forms[0] : forms[1];
}

export function pluralize(count: number, forms: [string, string, string]): string {
  const absCount = Math.abs(count);
  const mod10 = absCount % 10;
  const mod100 = absCount % 100;

  if (mod100 >= 11 && mod100 <= 19) {
    return forms[2]; // 11-19 always use plural form
  }
  if (mod10 === 1) {
    return forms[0]; // 1, 21, 31, etc.
  }
  if (mod10 >= 2 && mod10 <= 4) {
    return forms[1]; // 2-4, 22-24, etc.
  }
  return forms[2]; // 0, 5-20, 25-30, etc.
}

/**
 * Shared date formatting utility that respects the active UI language.
 * Uses ru-RU locale for Russian and en-US locale for English.
 */
export function formatDate(dateStr: string, language: 'ru' | 'en', options?: Intl.DateTimeFormatOptions): string {
  const locale = language === 'ru' ? 'ru-RU' : 'en-US';
  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  };
  return new Date(dateStr).toLocaleDateString(locale, options ?? defaultOptions);
}
